import _ from "lodash";
/** external */
import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";
/** internal */
import {
  deleteQuestionOrganizationResource,
  duplicateQuestionInWorksheet,
  updateVisibleQuestionAssistantIds,
  deleteQuestionV2,
  deleteQuestion,
  duplicateQuestionInWorksheetV2,
  updateWorksheetSidebar,
} from "Worksheet/modules/WorksheetModules";
import { useI18n } from "Hooks";
import Question from "Worksheet/components/Question";
import {
  getFormattedQuestion,
  getAssessmentDetailsData,
  getLearningGoalsFields,
  getOptionsFromField,
  getQuestionTitle,
} from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  getIsLearningGoalsRevampEnabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  WORKSHEET_MODES,
  MODE_TEACHER_CREATE,
  FIELD_UID_ADD_LEARNING_GOALS,
  BLANKS_ANSWER_REGEX,
  AI_GEN_IDENTIFIER,
  MODE_ASSESSMENT_VIEW,
} from "Worksheet/modules/WorksheetConstants";
import {
  getActiveTab,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getSelectedCurriculumId,
  getUserType,
} from "modules/CommonSelectors";
import { getIsFromClassroom } from "UnitPlans/modules/Utils";
import {
  getDynamicTemplate,
  updateAssessmentTemplate,
} from "Assessments/modules/Utils";
import { TODDLE_LIBRARY } from "Constants/stringConstants";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import { getAddLearningGoalsDropdownOptions } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import { createAIConversation } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";
import { generateRandomId } from "Utils";
import { getAssessmentLearningGoalsDropdownOptions } from "AppComponents/AssessmentLearningGoals/module/AssessmentLearningGoalsUtils";

/** relative */
import classes from "./QuestionDetails.scss";

const QuestionDetails = props => {
  const {
    mode,
    setQuestionRef,
    portalType,
    showWorksheetAssistant,
    sectionId,
    deleteQuestionOrganizationResource,
    deleteQuestionV2,
    deleteQuestion,
    duplicateQuestionInWorksheet,
    learningGoalsDropdownOptions,
    learningGoalsFields,
    selectedQuestion,
    getLearningGoalsMappingWrapper,
    createAIConversation,
    questionIndex,
    updateVisibleQuestionAssistantIds,
    visibleQuestionAssistantIds,
    isAiGeneratedQuestion,
    fieldList,
    updateWorksheetSidebar,
    isSeparatorQuestion,
    spaceContainerInBetweenRef,
    isAIAssessmentToolsOpen,
    getAddButton,
    addQuestionsInBetweenProps,
    duplicateQuestionInWorksheetV2,
    parentItemId, // section's item id
    isQueriesV2Enabled,
    unitPlanTemplate,
  } = props;

  const { t } = useI18n();

  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();

  const deleteSectionChildResource = useCallback(
    (childResourceId, questionId) => {
      // Use V2 deletion when mutations V2 is enabled (both questions and sections are in flat structure)
      if (isMutationsV2Enabled) {
        isQueriesV2Enabled
          ? deleteQuestionV2({
              questionId,
            })
          : deleteQuestion({
              questionId,
              parentId: sectionId,
            });
      } else {
        // Fall back to old implementation
        deleteQuestionOrganizationResource({
          id: childResourceId,
          parentId: sectionId,
        });
      }
    },
    [
      deleteQuestionOrganizationResource,
      deleteQuestionV2,
      deleteQuestion,
      sectionId,
    ]
  );

  const hasQuestion = !_.isEmpty(selectedQuestion);
  const selectedQuestionId = _.get(selectedQuestion, "id", "");
  const questionAIConversationId = _.get(
    selectedQuestion,
    "questionData.aiConversation.edges.0.node.id",
    ""
  );

  const questionForAssistant = useMemo(() => {
    const questionData = _.get(selectedQuestion, "questionData", {});

    let question = {};
    if (!_.isEmpty(questionData)) {
      const questionId = _.get(questionData, "id");

      const { htmlString } = getQuestionTitle({ question: questionData, t });

      const questionResourceId = _.get(selectedQuestion, "resourceId");

      const questionTypeUID = _.get(questionData, "questionType.uid");

      // * handling issue for blanks
      const questionTitle = _.replace(
        htmlString,
        BLANKS_ANSWER_REGEX,
        "_______"
      );

      question = {
        questionId,
        questionIndex,
        questionTitle, // title in html
        sectionId,
        questionAIConversationId,
        questionResourceId,
        questionTypeUID,
        isBlankQuestion: _.isEmpty(questionTitle),
      };
    }

    return question;
  }, [questionAIConversationId, questionIndex, sectionId, selectedQuestion, t]);

  // * creating array of learning goals fields data from selected question's fields
  const getLearningGoalsFieldsData = useCallback(() => {
    const learningGoalsFieldsKeys = _.map(learningGoalsDropdownOptions, "key");
    const questionFieldsData = _.get(selectedQuestion, "questionFields", []);

    const propertiesToBeRemove = ["__typename", "resolveFields", "id"];

    // * filtering learning goals fields and omiting properties
    const learningGoalsFields = _.filter(questionFieldsData, field =>
      _.includes(learningGoalsFieldsKeys, _.get(field, "uid"))
    ).map(field => ({
      id: generateRandomId(),
      ..._.omit(field, propertiesToBeRemove),
    }));

    return learningGoalsFields;
  }, [learningGoalsDropdownOptions, selectedQuestion]);

  const handleShowQuestionAssistant = useCallback(
    (showAssistant = true) => {
      if (_.isEmpty(questionAIConversationId)) {
        const learningGoalsFieldsData = getLearningGoalsFieldsData();

        createAIConversation({
          input: {
            entityType: "QUESTION",
            entityId: selectedQuestionId,
            metadata: {
              allFields: learningGoalsFieldsData,
              localFields: [],
            },
          },
          isQuestionAssistant: true,
        });
      }

      const activeQuestionId = _.get(selectedQuestion, "questionData.id", "");
      const newVisibleIds = [...visibleQuestionAssistantIds];
      newVisibleIds.push(activeQuestionId);
      updateVisibleQuestionAssistantIds(newVisibleIds);
      // toggling sidebar on smaller screens for better visibility when AiQuestionAssistant is opened
      if (window.innerWidth < 1150) {
        updateWorksheetSidebar({ isOpen: !showAssistant });
      }
    },
    [
      questionAIConversationId,
      selectedQuestion,
      visibleQuestionAssistantIds,
      updateVisibleQuestionAssistantIds,
      getLearningGoalsFieldsData,
      createAIConversation,
      selectedQuestionId,
      updateWorksheetSidebar,
    ]
  );

  const questionDetailsClasses = classNames(classes.questionDetailsContainer, {
    [classes.scrollContent]: !isSeparatorQuestion,
  });

  const duplicateSectionChildResource = useCallback(
    async ({
      questionId,
      questionResourceId,
      parentResourceId,
      isSubQuestion,
      parentItemId,
    }) => {
      if (isMutationsV2Enabled) {
        return await duplicateQuestionInWorksheetV2({
          questionIds: [questionId],
          parentItemId,
          putAfterItemId: questionId,
          isQueriesV2Enabled,
        });
      }
      return await duplicateQuestionInWorksheet({
        questionId,
        questionResourceId,
        parentResourceId: parentResourceId || sectionId,
        isSubQuestion,
      });
    },
    [
      sectionId,
      duplicateQuestionInWorksheet,
      duplicateQuestionInWorksheetV2,
      isMutationsV2Enabled,
    ]
  );
  const questionFeedClasses = classNames(classes.feedContainer, {
    [classes.feedContainerRevamped]: mode === MODE_TEACHER_CREATE,
  });

  // Learning goals mapping, every goalId is mapped to an array which has title, questionType and id of the question
  const learningGoalsMapping = getLearningGoalsMappingWrapper(
    learningGoalsFields
  );

  return (
    <div className={questionDetailsClasses}>
      {hasQuestion ? (
        <div className={questionFeedClasses}>
          {spaceContainerInBetweenRef && (
            <span ref={spaceContainerInBetweenRef}></span>
          )}
          <Question
            mode={isAiGeneratedQuestion ? MODE_ASSESSMENT_VIEW : mode}
            onDeleteClick={deleteSectionChildResource}
            setQuestionRef={setQuestionRef}
            isFromLibrary={false}
            portalType={portalType}
            {...selectedQuestion}
            showWorksheetAssistant={showWorksheetAssistant}
            learningGoalsDropdownOptions={learningGoalsDropdownOptions}
            learningGoalsFields={learningGoalsFields}
            learningGoalsMapping={learningGoalsMapping}
            getLearningGoalsMappingWrapper={getLearningGoalsMappingWrapper}
            handleShowQuestionAssistant={handleShowQuestionAssistant}
            duplicateSectionChildResource={duplicateSectionChildResource}
            fieldList={fieldList}
            isAIAssessmentToolsOpen={isAIAssessmentToolsOpen}
            getAddButton={getAddButton}
            addQuestionsInBetweenProps={addQuestionsInBetweenProps}
            parentItemId={parentItemId}
            unitPlanTemplate={unitPlanTemplate}
          />
        </div>
      ) : null}
    </div>
  );
};

const mapActionCreators = {
  deleteQuestionOrganizationResource,
  duplicateQuestionInWorksheet,
  createAIConversation,
  updateVisibleQuestionAssistantIds,
  updateWorksheetSidebar,
  deleteQuestionV2,
  deleteQuestion,
  duplicateQuestionInWorksheetV2,
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    sourceFrom,
    classSubjects,
    assessmentId,
    creationMode,
    mode,
    unitPlanTemplateId,
    questionData,
    questionIndex,
    isQueriesV2Enabled,
  } = ownProps;

  //WORKSHEET_RESTRUCTURING: Use questionData directly when queriesV2 is enabled
  const subQuestions = isQueriesV2Enabled
    ? _.get(questionData, "children", [])
    : _.map(_.get(questionData, "childrenV2.edges", []), "node");

  const selectedQuestion =
    !_.isEmpty(questionData) &&
    getFormattedQuestion({
      question: questionData,
      mode,
      questionIndex,
      subQuestions,
      t,
    });

  const selectedQuestionFields = _.get(selectedQuestion, "questionFields", []);

  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const {
    assessmentType,
    worksheetTemplateId,
    unitType,
    unitPlanField,
    subjectsField,
    assessmentTools,
  } = getAssessmentDetailsData({ assessmentId, collaboratorsFilter });

  const worksheetTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [worksheetTemplateId] },
    false
  );

  const unitPlanTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [unitPlanTemplateId] },
    false
  );

  const worksheetTemplate = _.get(worksheetTemplateCacheData, "body", {});
  const unitPlanTemplate = _.get(unitPlanTemplateCacheData, "body", {});

  const isFromClassroom = getIsFromClassroom({
    sourceFrom,
    template: worksheetTemplate,
  });

  const isToddleLibrary = getActiveTab(state) == TODDLE_LIBRARY;

  const curriculumType = isToddleLibrary
    ? _.get(state, "classRoom.curriculumProgram.type", "")
    : getCurrentCurriculumProgramType(state);

  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    []
  );

  const originalTemplate = getDynamicTemplate({
    template: worksheetTemplate,
    unitType,
    t,
    curriculumType,
    plannerElementSets,
    subjects: classSubjects,
    creationMode,
    curriculumId: getSelectedCurriculumId(state),
    templateId: worksheetTemplateId,
  });

  /** unit plan & subjects field needed for show unit/subjects based data in learning goals */
  const resourceFields = [
    ...selectedQuestionFields,
    unitPlanField,
    subjectsField,
  ];

  const updatedTemplate = updateAssessmentTemplate({
    t,
    template: originalTemplate,
    assessmentType,
    isFromClassroom,
    curriculumType,
    resourceFields,
    mode: "edit",
    templateId: worksheetTemplateId,
    plannerElementSets,
    subjects: classSubjects,
    userType: getUserType(state),
    assessmentTools,
  });

  const fieldList = _.get(updatedTemplate, "field_list", {});

  const defaultOptions = getOptionsFromField({
    fieldList,
    fieldUid: FIELD_UID_ADD_LEARNING_GOALS,
    t,
  });

  const isLearningGoalsRevampField = getIsLearningGoalsRevampEnabled();

  const learningGoalsOptions = getAddLearningGoalsDropdownOptions({
    options: defaultOptions,
    resourceFields,
    unitPlanTemplate,
    subjects: classSubjects,
    fieldList,
  });

  const learningGoalsDropdownOptions = isLearningGoalsRevampField
    ? getAssessmentLearningGoalsDropdownOptions({
        t,
        learningGoalsOptions,
        curriculumType,
      })
    : learningGoalsOptions;

  const learningGoalsFields = getLearningGoalsFields(updatedTemplate);

  const visibleQuestionAssistantIds = _.get(
    state,
    "worksheet.visibleQuestionAssistantIds",
    []
  );
  const activeQuestionId = _.get(state, "worksheet.activeQuestion");
  const isAiGeneratedQuestion = _.startsWith(
    activeQuestionId,
    AI_GEN_IDENTIFIER
  );

  return {
    learningGoalsDropdownOptions,
    learningGoalsFields,
    selectedQuestion,
    visibleQuestionAssistantIds,
    isAiGeneratedQuestion,
    fieldList,
    unitPlanTemplate,
  };
};

export default connect(mapStateToProps, mapActionCreators)(QuestionDetails);

QuestionDetails.displayName = "QuestionDetails";

QuestionDetails.propTypes = {
  mode: PropTypes.oneOf(WORKSHEET_MODES),
  setQuestionRef: PropTypes.func,
  sectionId: PropTypes.string,
  questionIndex: PropTypes.number,
  sourceFrom: PropTypes.string,
  classSubjects: PropTypes.arrayOf(PropTypes.object),
  creationMode: PropTypes.string,
  unitPlanTemplateId: PropTypes.string,
  assessmentId: PropTypes.string,
  t: PropTypes.func,
  portalType: PropTypes.string,
  showWorksheetAssistant: PropTypes.bool,
  courseId: PropTypes.string,
  selectedCourse: PropTypes.object,
  unitPlanId: PropTypes.string,
  isFromUnitPlan: PropTypes.bool,
  isDraft: PropTypes.bool,
  isSeparatorQuestion: PropTypes.bool,
  spaceContainerInBetweenRef: PropTypes.shape({
    current: PropTypes.instanceOf(HTMLElement),
  }),
  getAddButton: PropTypes.func,
  addQuestionsInBetweenProps: PropTypes.object,
  parentItemId: PropTypes.string,
  duplicateQuestionInWorksheetV2: PropTypes.func,
  isWorksheetMutationsV2Enabled: PropTypes.func,
  isQueriesV2Enabled: PropTypes.bool,
};

QuestionDetails.defaultProps = {
  mode: MODE_TEACHER_CREATE,
  setQuestionRef: _.noop,
  t: _.noop,
  selectedCourse: {},
  isFromUnitPlan: false,
  isDraft: false,
  isSeparatorQuestion: false,
  spaceContainerInBetweenRef: null,
  getAddButton: _.noop,
  addQuestionsInBetweenProps: {},
  duplicateQuestionInWorksheetV2: _.noop,
  isWorksheetMutationsV2Enabled: _.noop,
  isQueriesV2Enabled: false,
};
